import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Divider,
  Paper, Stack, Accordion, AccordionSummary, AccordionDetails,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Link, CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  fetchAction, fetchTask, deleteAction,
  fetchActionGuidelines, createGuideline, updateGuideline, deleteGuideline,
} from "../../api/companyApi";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

// ── ガイドライン 追加/編集ダイアログ ─────────────────────────
function GuidelineFormDialog({ open, onClose, onSave, editData, intl }) {
  const t = (id) => intl.formatMessage({ id });
  const [guidelineName, setGuidelineName] = useState("");
  const [guidelineDescription, setGuidelineDescription] = useState("");
  const [guidelineUrl, setGuidelineUrl] = useState("");

  useEffect(() => {
    if (editData) {
      setGuidelineName(editData.guidelineName || "");
      setGuidelineDescription(editData.guidelineDescription || "");
      setGuidelineUrl(editData.url || "");
    } else {
      setGuidelineName("");
      setGuidelineDescription("");
      setGuidelineUrl("");
    }
  }, [editData, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
      <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
        {editData ? t("action.guideline.edit.title") : t("action.guideline.add.title")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t("action.guideline.name")} value={guidelineName} onChange={(e) => setGuidelineName(e.target.value)} size="small" fullWidth required />
          <TextField label={t("action.guideline.description")} value={guidelineDescription} onChange={(e) => setGuidelineDescription(e.target.value)} size="small" fullWidth multiline rows={4} />
          <TextField label={t("action.guideline.url")} value={guidelineUrl} onChange={(e) => setGuidelineUrl(e.target.value)} size="small" fullWidth placeholder="https://..." />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={() => onSave({ ...(editData || {}), guidelineName: guidelineName.trim(), guidelineDescription: guidelineDescription.trim(), url: guidelineUrl.trim() || null })} disabled={!guidelineName.trim()}>
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── メインコンポーネント ─────────────────────────────────────
export default function DepartmentActionDetailPage() {
  const { businessDomainId, businessUnitId, departmentId, actionId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions/${actionId}`;
  const listPath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}/actions`;

  const [action, setAction] = useState(null);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // ガイドライン
  const [guidelines, setGuidelines] = useState([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [guidelineDialogOpen, setGuidelineDialogOpen] = useState(false);
  const [editingGuideline, setEditingGuideline] = useState(null);
  const [deleteGuidelineId, setDeleteGuidelineId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const act = await fetchAction(actionId);
        setAction(act);
        if (act.taskId) {
          try { setTask(await fetchTask(act.taskId)); } catch { /* ignore */ }
        }
      } catch (e) { notify.apiError(e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  const loadGuidelines = useCallback(async () => {
    setGuidelinesLoading(true);
    try {
      const data = await fetchActionGuidelines(actionId);
      setGuidelines(Array.isArray(data) ? data : []);
    } catch { setGuidelines([]); }
    finally { setGuidelinesLoading(false); }
  }, [actionId]);

  useEffect(() => { loadGuidelines(); }, [loadGuidelines]);

  const handleDelete = async () => {
    try {
      await deleteAction(actionId);
      notify.success(t("message.delete.success"));
      navigate(listPath, { replace: true });
    } catch (err) { notify.apiError(err); }
  };

  const handleGuidelineSave = async (formData) => {
    try {
      if (editingGuideline) {
        await updateGuideline({ ...formData, guidelineId: editingGuideline.guidelineId, companyId, targetId: actionId, guidelineTarget: "Action" });
        notify.success(t("action.guideline.updateSuccess"));
      } else {
        await createGuideline({ ...formData, companyId, targetId: actionId, guidelineTarget: "Action" });
        notify.success(t("action.guideline.createSuccess"));
      }
      setGuidelineDialogOpen(false);
      setEditingGuideline(null);
      loadGuidelines();
    } catch (err) { notify.apiError(err); }
  };

  const handleGuidelineDelete = async (id) => {
    try {
      await deleteGuideline(id);
      notify.success(t("action.guideline.deleteSuccess"));
      setDeleteGuidelineId(null);
      setGuidelines((prev) => prev.filter((g) => g.guidelineId !== id));
    } catch (err) { notify.apiError(err); }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>;
  if (!action) return null;

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("department.action.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`${basePath}/edit`)}>
            {t("common.edit")}
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirmOpen(true)}>
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      {/* アクション詳細 */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("department.action.name")} value={action.actionName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("common.description")} value={action.actionDescription} />
          </Grid>
          {task && (
            <Grid size={12}>
              <DetailField label={t("action.task")} value={task.taskName} />
            </Grid>
          )}
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />

      {/* ガイドライン セクション */}
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 700 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MenuBookIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("action.guideline.title")}</Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => { setEditingGuideline(null); setGuidelineDialogOpen(true); }}>
            {t("action.guideline.add.button")}
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {guidelinesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} /></Box>
        ) : guidelines.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t("action.guideline.empty")}</Typography>
        ) : (
          <Stack spacing={1}>
            {guidelines.map((g, i) => (
              <Accordion key={g.guidelineId || i} disableGutters elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexGrow: 1, mr: 1 }}>
                    <MenuBookIcon fontSize="small" color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1 }}>{g.guidelineName}</Typography>
                    <Tooltip title={t("common.edit")}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingGuideline(g); setGuidelineDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete")}>
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteGuidelineId(g.guidelineId); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {g.guidelineDescription && (
                    <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: "pre-wrap", lineHeight: 1.8, color: "text.secondary" }}>
                      {g.guidelineDescription}
                    </Typography>
                  )}
                  {g.url && (
                    <Button variant="outlined" size="small" startIcon={<OpenInNewIcon />} component={Link} href={g.url} target="_blank" rel="noopener noreferrer">
                      {t("action.guideline.openUrl")}
                    </Button>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(listPath)}>{t("common.back")}</Button>
      </Box>

      {/* ガイドライン 追加/編集ダイアログ */}
      <GuidelineFormDialog open={guidelineDialogOpen} onClose={() => { setGuidelineDialogOpen(false); setEditingGuideline(null); }} onSave={handleGuidelineSave} editData={editingGuideline} intl={intl} />

      {/* ガイドライン 削除確認 */}
      <ConfirmDialog open={!!deleteGuidelineId} onClose={() => setDeleteGuidelineId(null)} onConfirm={() => handleGuidelineDelete(deleteGuidelineId)} title={t("action.guideline.deleteConfirm.title")} message={t("action.guideline.deleteConfirm.message")} />

      {/* アクション 削除確認 */}
      <ConfirmDialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} onConfirm={handleDelete} title={t("common.delete")} message={t("message.delete.confirm")} />
    </Box>
  );
}
